import React, { useEffect, useMemo, useState } from "react";

import { ChevronDown, ChevronRight, Loader2, Plus, Trash2 } from "lucide-react";

import Button from "../../../Components/Button";
import { useToast } from "../../../Components/Toast";
import ToggleSwitch from "../../../Components/ToggleSwitch";
import { roConfigErrorMessage, useRoConfig } from "../../../Hooks/useRoConfig";
import { RoMetricItem, RoWeightItem } from "../../../Types/RoConfig";

type DimRow = RoWeightItem & { defaultWeightPct: number };
type MetricRow = RoMetricItem & { defaultWeightPct: number };

const round1 = (n: number) => Math.round(n * 10) / 10;

const slugify = (name: string) =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const DimensionWeightCard: React.FC<{
  row: DimRow;
  onToggle: () => void;
  onWeightChange: (pct: number) => void;
}> = ({ row, onToggle, onWeightChange }) => (
  <div
    className={`rounded-xl border px-3.5 py-3 ${
      row.included ? "border-gray-200 bg-white" : "border-gray-200 bg-gray-50"
    }`}
  >
    <div className="mb-2 flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2">
        <ToggleSwitch
          id={`ro-weight-dim-${row.key}`}
          checked={row.included}
          onChange={onToggle}
        />
        <span className="truncate text-sm font-medium text-gray-900">
          <span className="mr-1 font-mono text-xs text-gray-500">
            {row.key}
          </span>
          {row.label}
        </span>
      </div>
      <span className="shrink-0 text-[11px] text-gray-400">
        default {round1(row.defaultWeightPct)}
      </span>
    </div>
    <input
      type="number"
      min={0}
      max={100}
      value={row.weightPct}
      disabled={!row.included}
      onChange={e => onWeightChange(Number(e.target.value) || 0)}
      className="w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-sm text-gray-900 outline-none disabled:bg-gray-100 disabled:text-gray-400"
    />
  </div>
);

const MetricWeightCard: React.FC<{
  row: MetricRow;
  busy?: boolean;
  onToggle: () => void;
  onWeightChange: (pct: number) => void;
  onRemove: () => void;
}> = ({ row, busy, onToggle, onWeightChange, onRemove }) => (
  <div
    className={`rounded-lg border px-3 py-2.5 ${
      row.included ? "border-gray-200 bg-white" : "border-gray-200 bg-gray-50"
    }`}
  >
    <div className="mb-1.5 flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2">
        <ToggleSwitch
          id={`ro-weight-metric-${row.metricId}`}
          checked={row.included}
          onChange={onToggle}
        />
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-gray-900">
            {row.metricName || row.metricId}
          </div>
          {row.metricName && row.metricName !== row.metricId && (
            <div className="truncate font-mono text-[11px] text-gray-400">
              {row.metricId}
            </div>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <span className="text-[11px] text-gray-400">
          default {round1(row.defaultWeightPct)}
        </span>
        <button
          type="button"
          disabled={busy}
          onClick={onRemove}
          className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
          title="Remove metric"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
    <input
      type="number"
      min={0}
      max={100}
      value={row.weightPct}
      disabled={!row.included}
      onChange={e => onWeightChange(Number(e.target.value) || 0)}
      className="w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-sm text-gray-900 outline-none disabled:bg-gray-100 disabled:text-gray-400"
    />
  </div>
);

const AddMetricForm: React.FC<{
  onAdd: (name: string) => void;
  onCancel: () => void;
}> = ({ onAdd, onCancel }) => {
  const [name, setName] = useState("");
  return (
    <div className="col-span-full flex flex-wrap items-center gap-2 rounded-lg border border-violet-200 bg-violet-50/40 px-3 py-2.5">
      <input
        autoFocus
        type="text"
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder="New metric name"
        className="min-w-[12rem] flex-1 rounded-md border border-violet-300 px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-violet-500/30"
        onKeyDown={e => {
          if (e.key === "Enter" && name.trim()) onAdd(name.trim());
          if (e.key === "Escape") onCancel();
        }}
      />
      <button
        type="button"
        disabled={!name.trim()}
        onClick={() => name.trim() && onAdd(name.trim())}
        className="rounded-md bg-violet-600 px-2.5 py-1.5 text-xs font-medium text-white disabled:opacity-40"
      >
        Add
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600"
      >
        Cancel
      </button>
    </div>
  );
};

const MetricAccordion: React.FC<{
  dim: DimRow;
  rows: MetricRow[];
  expanded: boolean;
  busy: boolean;
  onToggleExpand: () => void;
  onToggleMetric: (metricId: string) => void;
  onMetricWeightChange: (metricId: string, pct: number) => void;
  onAddMetric: (name: string) => void;
  onRemoveMetric: (metricId: string) => void;
}> = ({
  dim,
  rows,
  expanded,
  busy,
  onToggleExpand,
  onToggleMetric,
  onMetricWeightChange,
  onAddMetric,
  onRemoveMetric,
}) => {
  const [adding, setAdding] = useState(false);
  const enabledCount = rows.filter(r => r.included).length;
  const total = round1(
    rows.filter(r => r.included).reduce((sum, r) => sum + r.weightPct, 0)
  );

  const handleAdd = (name: string) => {
    onAddMetric(name);
    setAdding(false);
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200">
      <button
        type="button"
        onClick={onToggleExpand}
        className="flex w-full items-center justify-between gap-2 bg-gray-50 px-4 py-2.5 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="font-mono text-xs text-gray-500">{dim.key}</span>
          {dim.label}
          <span className="text-xs font-normal text-gray-500">
            ({enabledCount}/{rows.length} included)
          </span>
          {!dim.included && (
            <>
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
                optional
              </span>
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
                dimension off
              </span>
            </>
          )}
        </span>
        <span className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-xs font-medium text-gray-600">
          Total: {total.toFixed(1)}
        </span>
      </button>
      {expanded && (
        <div className="p-4">
          <div className="mb-3 flex justify-end">
            <button
              type="button"
              disabled={busy || adding}
              onClick={() => setAdding(true)}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
            >
              <Plus size={14} />
              Add metric
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map(row => (
              <MetricWeightCard
                key={row.metricId}
                row={row}
                busy={busy}
                onToggle={() => onToggleMetric(row.metricId)}
                onWeightChange={pct => onMetricWeightChange(row.metricId, pct)}
                onRemove={() => onRemoveMetric(row.metricId)}
              />
            ))}
            {adding && (
              <AddMetricForm
                onAdd={handleAdd}
                onCancel={() => setAdding(false)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CompositeSection: React.FC<{
  title: string;
  churnLine: string;
  dims: DimRow[];
  setDims: React.Dispatch<React.SetStateAction<DimRow[]>>;
  metrics: MetricRow[];
  setMetrics: React.Dispatch<React.SetStateAction<MetricRow[]>>;
  metricCount: number;
  onSave: () => void;
  saving: boolean;
  busy: boolean;
}> = ({
  title,
  churnLine,
  dims,
  setDims,
  metrics,
  setMetrics,
  metricCount,
  onSave,
  saving,
  busy,
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const dimTotal = round1(
    dims.filter(d => d.included).reduce((s, d) => s + d.weightPct, 0)
  );

  const toggleDim = (key: string) =>
    setDims(prev =>
      prev.map(d => (d.key === key ? { ...d, included: !d.included } : d))
    );
  const setDimWeight = (key: string, pct: number) =>
    setDims(prev =>
      prev.map(d => (d.key === key ? { ...d, weightPct: pct } : d))
    );
  const toggleMetric = (metricId: string) =>
    setMetrics(prev =>
      prev.map(m =>
        m.metricId === metricId ? { ...m, included: !m.included } : m
      )
    );
  const setMetricWeight = (metricId: string, pct: number) =>
    setMetrics(prev =>
      prev.map(m => (m.metricId === metricId ? { ...m, weightPct: pct } : m))
    );
  const addMetric = (dim: DimRow, name: string) => {
    const metricId = `${slugify(name) || "metric"}_${Date.now().toString(36)}`;
    setMetrics(prev => [
      ...prev,
      {
        dimensionKey: dim.key,
        dimensionLabel: dim.label,
        category: dim.category,
        metricId,
        metricName: name,
        included: true,
        sortOrder: prev.filter(m => m.dimensionKey === dim.key).length + 1,
        weightPct: 0,
        defaultWeightPct: 0,
      },
    ]);
    setExpanded(prev => new Set(prev).add(dim.key));
  };
  const removeMetric = (metricId: string) =>
    setMetrics(prev => prev.filter(m => m.metricId !== metricId));
  const toggleExpand = (key: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const sectionSave = (
    <Button variant="primary" onClick={onSave} disabled={busy} loading={saving}>
      Save
    </Button>
  );

  return (
    <div className="mb-8 last:mb-0">
      <h3 className="mb-1 text-base font-semibold text-gray-900">{title}</h3>
      <p className="mb-4 text-sm text-gray-500">
        {dims.length} dimensions, {metricCount} KPIs. {churnLine}
      </p>

      <div className="mb-5 rounded-xl border border-gray-200 p-4">
        <div className="mb-1 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h4 className="mb-0 text-sm font-semibold text-gray-900">
              Dimension weights
            </h4>
            <p className="mb-0 mt-1 text-xs text-gray-500">
              Toggle a dimension to include/exclude. Included weights should
              total ~100 per scope.
            </p>
          </div>
          {sectionSave}
        </div>
        <div className="mb-3 mt-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700">
            Total: {dimTotal.toFixed(1)}
          </span>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {dims.map(d => (
            <DimensionWeightCard
              key={d.key}
              row={d}
              onToggle={() => toggleDim(d.key)}
              onWeightChange={pct => setDimWeight(d.key, pct)}
            />
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 p-4">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h4 className="mb-0 text-sm font-semibold text-gray-900">
              Per-KPI intra-dimension weights
            </h4>
            <p className="mb-0 mt-1 text-xs text-gray-500">
              Toggle include/exclude, add or remove metrics. Included metrics
              within each dimension should sum to ~100. Save to persist.
            </p>
          </div>
          {sectionSave}
        </div>
        <div className="space-y-2.5">
          {dims.map(d => (
            <MetricAccordion
              key={d.key}
              dim={d}
              rows={metrics.filter(m => m.dimensionKey === d.key)}
              expanded={expanded.has(d.key)}
              busy={busy}
              onToggleExpand={() => toggleExpand(d.key)}
              onToggleMetric={toggleMetric}
              onMetricWeightChange={setMetricWeight}
              onAddMetric={name => addMetric(d, name)}
              onRemoveMetric={removeMetric}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

function hydrateRows(
  dimensionWeights: RoWeightItem[],
  metrics: RoMetricItem[]
) {
  return {
    riskDims: dimensionWeights
      .filter(d => d.category === "risk")
      .map(d => ({ ...d, defaultWeightPct: d.weightPct })),
    opportunityDims: dimensionWeights
      .filter(d => d.category === "opportunity")
      .map(d => ({ ...d, defaultWeightPct: d.weightPct })),
    riskMetrics: metrics
      .filter(m => m.category === "risk")
      .map(m => ({ ...m, defaultWeightPct: m.weightPct })),
    opportunityMetrics: metrics
      .filter(m => m.category === "opportunity")
      .map(m => ({ ...m, defaultWeightPct: m.weightPct })),
  };
}

const RoWeightsPanel: React.FC<{
  onHeaderActionsChange?: (node: React.ReactNode) => void;
}> = ({ onHeaderActionsChange }) => {
  const toast = useToast();
  const { dimensionWeights, metrics, loading, error, saveConfig, resetConfig } =
    useRoConfig();

  const [riskDims, setRiskDims] = useState<DimRow[]>([]);
  const [opportunityDims, setOpportunityDims] = useState<DimRow[]>([]);
  const [riskMetrics, setRiskMetrics] = useState<MetricRow[]>([]);
  const [opportunityMetrics, setOpportunityMetrics] = useState<MetricRow[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (!loading) {
      const next = hydrateRows(dimensionWeights, metrics);
      setRiskDims(next.riskDims);
      setOpportunityDims(next.opportunityDims);
      setRiskMetrics(next.riskMetrics);
      setOpportunityMetrics(next.opportunityMetrics);
      setHydrated(true);
    }
  }, [loading, dimensionWeights, metrics]);

  const riskMetricCount = useMemo(
    () => riskMetrics.length,
    [riskMetrics.length]
  );
  const opportunityMetricCount = useMemo(
    () => opportunityMetrics.length,
    [opportunityMetrics.length]
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const dimensions = [...riskDims, ...opportunityDims].map(d => ({
        key: d.key,
        category: d.category,
        included: d.included,
        weight_pct: Number(d.weightPct) || 0,
      }));
      const metricPayload = [...riskMetrics, ...opportunityMetrics].map(m => ({
        dimension_key: m.dimensionKey,
        category: m.category,
        metric_id: m.metricId,
        included: m.included,
        weight_pct: Number(m.weightPct) || 0,
      }));
      await saveConfig({ dimensions, metrics: metricPayload });
      toast.success(
        "R&O config saved. Re-run Data Hub R&O scores to apply the new weights."
      );
    } catch (e: unknown) {
      toast.error(roConfigErrorMessage(e, "Failed to save R&O config."));
      const next = hydrateRows(dimensionWeights, metrics);
      setRiskDims(next.riskDims);
      setOpportunityDims(next.opportunityDims);
      setRiskMetrics(next.riskMetrics);
      setOpportunityMetrics(next.opportunityMetrics);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (
      !window.confirm(
        "Reset R&O config to seeded defaults? This replaces current weights and include flags."
      )
    ) {
      return;
    }
    setResetting(true);
    try {
      await resetConfig();
      toast.success("R&O config reset to defaults.");
    } catch (e: unknown) {
      toast.error(roConfigErrorMessage(e, "Failed to reset R&O config."));
    } finally {
      setResetting(false);
    }
  };

  const busy = saving || resetting;

  const headerActions = useMemo(
    () => (
      <Button
        variant="outline-primary"
        onClick={() => void handleReset()}
        disabled={busy || loading || !hydrated}
        loading={resetting}
      >
        Reset defaults
      </Button>
    ),
    // handleReset closes over latest resetConfig/toast; busy/loading gates the button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busy, loading, hydrated, resetting]
  );

  useEffect(() => {
    onHeaderActionsChange?.(headerActions);
    return () => onHeaderActionsChange?.(null);
  }, [headerActions, onHeaderActionsChange]);

  if (loading || !hydrated) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading R&amp;O weights…
      </div>
    );
  }

  if (error) {
    return <p className="py-8 text-sm text-red-600">{error}</p>;
  }

  const saveProps = {
    onSave: () => void handleSave(),
    saving,
    busy,
  };

  return (
    <div>
      <CompositeSection
        title="Risk Composite Score (RCS)"
        churnLine="RCS is risk health — higher score = lower risk."
        dims={riskDims}
        setDims={setRiskDims}
        metrics={riskMetrics}
        setMetrics={setRiskMetrics}
        metricCount={riskMetricCount}
        {...saveProps}
      />
      <CompositeSection
        title="Opportunity Composite Score (OCS)"
        churnLine="Higher score = stronger expansion readiness."
        dims={opportunityDims}
        setDims={setOpportunityDims}
        metrics={opportunityMetrics}
        setMetrics={setOpportunityMetrics}
        metricCount={opportunityMetricCount}
        {...saveProps}
      />
    </div>
  );
};

export default RoWeightsPanel;
